import React from 'react';
import { useLocation } from 'react-router-dom';
import { BarChart3, CheckSquare, Plug } from 'lucide-react';
import { TaskyTabs, TaskyTabItem } from '../tasky';
import { useMetaLang } from '../../i18n/metaReviewLang';

export const adOperationsPath = (path: string, search: string): string => {
  const source = new URLSearchParams(search);
  const next = new URLSearchParams();
  const product = source.get('product');
  if (product && /^[1-9]\d*$/.test(product)) next.set('product', product);
  const account = source.get('account');
  if (account && /^[1-9]\d*$/.test(account)) next.set('account', account);
  const lang = source.get('lang');
  if (lang === 'en' || lang === 'ja') next.set('lang', lang);
  const query = next.toString();
  return query ? `${path}?${query}` : path;
};

type TabKey = 'dashboard' | 'proposals' | 'meta' | 'google' | 'connections';

interface Props {
  active: TabKey;
  pendingCount?: number;
}

const TABS: { key: TabKey; path: string; icon: React.ReactNode }[] = [
  { key: 'dashboard', path: '/taap', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'proposals', path: '/taap/proposals', icon: <CheckSquare className="h-4 w-4" /> },
  { key: 'meta', path: '/ad-dashboard/meta', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'google', path: '/ad-dashboard/google', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'connections', path: '/settings/media-connect', icon: <Plug className="h-4 w-4" /> },
];

/**
 * TAAP 内の画面切り替え。
 *
 * 画面遷移なので button ではなくリンクで描く（中クリック・新しいタブで開くを保つ）。
 * 未承認件数をタブに出すのは、提案が溜まったまま気づかれないのを防ぐため。
 * 0 件でもバッジの幅を確保する（件数の増減でタブ幅が動かないようにする）。
 */
const TaapTabs: React.FC<Props> = ({ active, pendingCount = 0 }) => {
  const { search } = useLocation();
  const [lang] = useMetaLang();
  const labels = lang === 'en'
    ? ['Dashboard', 'Proposals & approvals', 'Meta Ads', 'Google Ads', 'Ad connections']
    : ['ダッシュボード', '提案・承認', 'Meta広告', 'Google広告', '広告連携'];

  const items: TaskyTabItem<TabKey>[] = TABS.map((tab, index) => ({
    value: tab.key,
    label: labels[index],
    icon: tab.icon,
    href: adOperationsPath(tab.path, search),
    count: tab.key === 'proposals' ? pendingCount : undefined,
  }));

  return (
    <div className="min-w-0 overflow-x-auto">
      <TaskyTabs<TabKey>
        aria-label={lang === 'en' ? 'Ad operations' : '広告運用'}
        items={items}
        value={active}
        className="min-w-max"
      />
    </div>
  );
};

export default React.memo<Props>(TaapTabs);
